import { useMemo, useRef } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import HeadNext from 'next/head';

import { JOB_FUNCTION_OPTIONS } from 'bx-forms/eas/getapp';
import BackToTop from 'components/BackToTop';
import Breadcrumb from 'components/Breadcrumb';
import ContainerWithArrowsBg from 'components/ContainerWithArrowsBg';
import Contentful from 'components/Contentful';
import getConfig from 'config/public';

// Custom field order to match prod: emailAddress, firstName, jobFunction (full width)
const BLOG_POST_FIELDS = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  { name: 'firstName', type: 'text', label: 'First Name' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
    fullWidth: true,
  },
];

const MultiFieldInlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.MultiFieldInlineForm),
  { ssr: false }
);
import Grid, { Container } from 'components/Grid';
import HomeLayout from 'components/Layout/Home';
import Seo from 'components/Layout/components/Seo';
import LazyContent from 'components/LazyContent';
import OneTapScript from 'components/OneTapScript';
import TrustBanner from 'components/TrustBanner';
import { useAppContext } from 'context/AppContext';
import { useUser } from 'hooks';
import useViewport from 'hooks/useViewport';
import { ROUTE_NAMES } from 'routes';
import routeService from 'services/routes';

import TopicList from '../components/TopicList';
import Authors from './components/Authors';
import ContentText from './components/ContentText';
import Head from './components/Head';
import RelatedPosts from './components/RelatedPosts';
import SocialIcons from './components/SocialIcons';
import TableOfContent from './components/TableOfContent';
import contentfulConfig from './contentfulConfig';
import { AppFetchContextProvider } from './useAppFetchContext';

import classes from './Post.module.scss';

const STRUCTURED_DATA_DATE_FORMAT = 'YYYY-MM-DD';

const hasInlineFaas = (content) =>
  !!Object.entries(content.links).find(
    (element) =>
      element[1] !== null && element[1]['__typename'] === 'InlineFaas'
  );

const getStructuredData = (post, host) => {
  const {
    url,
    headline,
    description,
    datePublished,
    dateModified,
    imageUrl,
    authors,
  } = {
    headline: post.title,
    imageUrl: post.titleImage?.url,
    description: post.shortDescription,
    dateModified: dayjs(post.sys.modifiedDate).format(
      STRUCTURED_DATA_DATE_FORMAT
    ),
    datePublished: dayjs(post.publishDate).format(STRUCTURED_DATA_DATE_FORMAT),
    url: routeService.generatePath(
      ROUTE_NAMES.RESOURCES_POST,
      { slug: post.slug },
      { domain: host }
    ),
    authors: post.authors.items,
  };

  const articleStructuredData = {
    '@context': 'http://schema.org',
    '@type': 'BlogPosting',
    url,
    headline,
    datePublished,
    dateModified,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    publisher: {
      '@type': 'Organization',
      name: 'GetApp',
      logo: {
        '@type': 'ImageObject',
        url: imageUrl,
      },
    },
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
    },
    description,
    author: authors.map(({ authorName, slug }) => ({
      '@type': 'Person',
      name: authorName,
      url: routeService.generatePath(
        ROUTE_NAMES.RESOURCES_AUTHOR,
        { slug },
        { domain: host }
      ),
    })),
  };
  return JSON.stringify(articleStructuredData);
};

/** @type {import('./types').default} */
const Post = ({ post, topics, relatedPosts, productSlugs }) => {
  const { up, is } = useViewport();
  const authorRef = useRef();
  const { host, isCypressTakingSnapshots, url } = useAppContext();
  const [user] = useUser();
  const shouldRenderRobotsMeta =
    getConfig().env === 'prod' && !url?.includes('?');

  const { tableOfContents: showTableOfContents } = post;

  const breadcrumbItems = useMemo(
    () => [
      {
        name: 'Blog & research',
        path: routeService.generatePath(ROUTE_NAMES.RESOURCES),
      },
      {
        name: post.title,
        path: routeService.generatePath(ROUTE_NAMES.RESOURCES_POST, {
          slug: post.slug,
        }),
      },
    ],
    [post]
  );

  return (
    <>
      <OneTapScript user={user} />
      <Seo
        title={post.title || 'GetApp.com'}
        description={post.shortDescription}
        image={post.titleImage?.url}
        route={{
          name: ROUTE_NAMES.RESOURCES_POST,
          params: { slug: post.slug },
        }}
        url={url}
      >
        {shouldRenderRobotsMeta && (
          <meta name="robots" content="index, follow" />
        )}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getStructuredData(post, host),
          }}
        />
      </Seo>
      <HeadNext>
        <link rel="preconnect" href="https://images.ctfassets.net/" />
        <link rel="dns-prefetch" href="https://images.ctfassets.net/" />
      </HeadNext>
      <AppFetchContextProvider productSlugs={productSlugs}>
        <ContainerWithArrowsBg>
          <Breadcrumb structuredDataItems={breadcrumbItems}>
            <Breadcrumb.Item
              href={routeService.generatePath(ROUTE_NAMES.RESOURCES)}
              action="go-to_resource_homepage"
            >
              Blog & research
            </Breadcrumb.Item>
            <Breadcrumb.Item isDisabled={true}>{post.title}</Breadcrumb.Item>
          </Breadcrumb>
          <Container className={classes.trustBannerContainer}>
            <TrustBanner />
          </Container>
          <Head post={post} authorRef={authorRef} />
          {showTableOfContents && (
            <>
              <Grid
                container={true}
                spacing={up('md') ? 4 : 0}
                className={classes.tableOfContentContainer}
              >
                {up('sm') && (
                  <Grid item={true} sm={1}>
                    <SocialIcons post={post} />
                  </Grid>
                )}
                <Grid
                  item={true}
                  xs={12}
                  sm={10}
                  md={8}
                  className={classes.tableOfContent}
                >
                  <TableOfContent content={post.content.json.content} />
                </Grid>
                {up('md') && <Grid item={true} md={3} />}
              </Grid>
            </>
          )}
          <Container
            fixedWidth={true}
            className={clsx('BlogPost', classes.pageContainer)}
          >
            <Grid container={true} spacing={4}>
              {up('sm') && (
                <Grid item={true} sm={1}>
                  {!showTableOfContents && <SocialIcons post={post} />}
                </Grid>
              )}
              <Grid item={true} sm={10} md={8}>
                <div className={classes.bodyContainer}>
                  <Contentful
                    document={post.content.json}
                    links={post.content.links}
                    getCustomNodes={contentfulConfig.getCustomNodes}
                    customEmbeddeds={contentfulConfig.customEmbeddeds}
                    renderText={(text) => <ContentText text={text} />}
                  />
                  {!isCypressTakingSnapshots &&
                    !hasInlineFaas(post.content) && (
                      <LazyContent minHeight={600}>
                        <MultiFieldInlineForm
                          formId="76LRG1T4cCiH2gQ0BEVNF"
                          gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                          fields={BLOG_POST_FIELDS}
                          headline="6 Steps to Finding the Right Software For You"
                          subHeading="Take the guesswork out of choosing the right software that will fit your needs. Use this guide to help you narrow down your options, fast!"
                          buttonText="Get the Guide"
                          successHeadline="Thank you, enjoy!"
                          successMessage="Your guide will be delivered to your inbox shortly."
                          contextProps={{
                            pageType: 'resources',
                            pagePlacement: 'sidebar',
                          }}
                          tracking={{
                            formName: 'DMBUY_2022_GA_Blog_Acq',
                            contentCategory: '',
                            contentRequest:
                              '6 Steps to Finding the Right Software For You',
                          }}
                        />
                      </LazyContent>
                    )}
                </div>
                {post.authors.items.length > 0 && (
                  <div ref={authorRef}>
                    <Authors authors={post.authors} />
                  </div>
                )}
              </Grid>
              <Grid item={true} xs={12}>
                {relatedPosts && post.topic && (
                  <RelatedPosts
                    posts={relatedPosts}
                    topicTitle={post.topic.name}
                  />
                )}
                <TopicList topics={topics.items} />
              </Grid>
            </Grid>
          </Container>
          <BackToTop>{is('xs') && <SocialIcons post={post} />}</BackToTop>
        </ContainerWithArrowsBg>
      </AppFetchContextProvider>
    </>
  );
};

Post.Layout = HomeLayout;

export default Post;
